import React, { useState } from 'react';
import { Eye, EyeOff, ArrowLeft, Check, AlertCircle, Loader } from 'lucide-react';
import { useStore } from '../store';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A',
  amber500:'#8B5A0A', ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF',
  red:'#DC2626', redBg:'#FEF2F2', green:'#16A34A', greenBg:'#F0FDF4',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

// ─── Shared global styles ─────────────────────────────────────────────────────
const GlobalStyles: React.FC = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans', sans-serif; background: #fff; color: #1A1814; }
    input:-webkit-autofill { -webkit-box-shadow: 0 0 0 40px #FFF4E0 inset !important; -webkit-text-fill-color: #1A1814 !important; }
    ::selection { background: #FFD88A88; }
    @keyframes spin  { to { transform: rotate(360deg); } }
    @keyframes fadeUp{ from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
    @keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-6px)} 40%,80%{transform:translateX(6px)} }
    .auth-grid   { display:grid; grid-template-columns:1fr 1fr; min-height:100vh; }
    .auth-card   { animation: fadeUp .4s ease both; }
    .shake       { animation: shake .35s ease; }
    .spin        { animation: spin .7s linear infinite; }
    @media(max-width:860px){ .auth-panel{display:none!important;} .auth-grid{grid-template-columns:1fr!important;} }
    input[type=text], input[type=email], input[type=password] {
      width:100%; height:48px; padding:0 46px 0 16px;
      border:1.5px solid ${C.border}; border-radius:10px;
      font-size:15px; font-family:'DM Sans',sans-serif;
      color:${C.ink}; background:${C.white}; outline:none;
      transition:border-color .15s, box-shadow .15s;
    }
    input[type=text]:focus, input[type=email]:focus, input[type=password]:focus {
      border-color:${C.amber300}; box-shadow:0 0 0 3px ${C.amber300}22;
    }
    input.err { border-color:${C.red}!important; box-shadow:0 0 0 3px ${C.red}18!important; }
  `}</style>
);

// ─── Jötr logo ───────────────────────────────────────────────────────────────
const Logo: React.FC = () => (
  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
    <div style={{ width:36, height:36, borderRadius:11, background:`linear-gradient(145deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <svg width="18" height="18" viewBox="0 0 180 180" fill="none">
        <g transform="translate(90,88) rotate(-35)">
          <rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/>
          <rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/>
          <polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/>
        </g>
        <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
        <g transform="translate(126,124) rotate(45)">
          <rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/>
        </g>
      </svg>
    </div>
    <span style={{ fontFamily:font.head, fontWeight:800, fontSize:22, letterSpacing:'-0.06em', color:C.ink }}>Jötr</span>
  </div>
);

// ─── Google icon ──────────────────────────────────────────────────────────────
const GoogleIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/>
    <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332Z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/>
  </svg>
);

// ─── Left panel ───────────────────────────────────────────────────────────────
const AuthPanel: React.FC<{ mode: 'login' | 'signup' }> = ({ mode }) => (
  <div className="auth-panel" style={{
    background:'linear-gradient(155deg, #1A1814 0%, #2C2416 60%, #3D2E10 100%)',
    display:'flex', flexDirection:'column', justifyContent:'space-between',
    padding:'48px 52px', position:'relative', overflow:'hidden',
  }}>
    <div style={{ position:'absolute', inset:0, opacity:0.04, backgroundImage:'radial-gradient(circle, #FAC775 1px, transparent 1px)', backgroundSize:'28px 28px', pointerEvents:'none' }}/>
    <div style={{ position:'absolute', top:-120, right:-80, width:400, height:400, borderRadius:'50%', background:`radial-gradient(circle,${C.amber300}33,transparent 70%)`, pointerEvents:'none' }}/>

    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:80 }}>
        <div style={{ width:36, height:36, borderRadius:11, background:`linear-gradient(145deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <svg width="18" height="18" viewBox="0 0 180 180" fill="none">
            <g transform="translate(90,88) rotate(-35)">
              <rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/>
              <rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/>
              <polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/>
            </g>
            <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
            <g transform="translate(126,124) rotate(45)"><rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/></g>
          </svg>
        </div>
        <span style={{ fontFamily:font.head, fontWeight:800, fontSize:22, letterSpacing:'-0.06em', color:C.white }}>Jötr</span>
      </div>

      <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(28px,2.8vw,38px)', color:C.white, letterSpacing:'-0.04em', lineHeight:1.12, marginBottom:20 }}>
        {mode === 'login'
          ? <>Welcome back.<br/><span style={{ color:C.amber200 }}>Your notes missed you.</span></>
          : <>Write it once.<br/><span style={{ color:C.amber200 }}>Find it forever.</span></>}
      </h2>
      <p style={{ fontSize:15, color:'rgba(255,255,255,0.5)', lineHeight:1.7, fontFamily:font.body, maxWidth:340 }}>
        {mode === 'login'
          ? 'Everything you wrote is right here — synced across all your devices, exactly as you left it.'
          : 'Join thousands of professionals and students who never lose a note. Fully free, forever.'}
      </p>
    </div>

    <div>
      <div style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:18, padding:'24px 28px', marginBottom:32 }}>
        <div style={{ display:'flex', gap:3, marginBottom:14 }}>
          {[0,1,2,3,4].map(i => <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={C.amber300}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>)}
        </div>
        <p style={{ fontSize:14, color:'rgba(255,255,255,0.75)', lineHeight:1.7, fontStyle:'italic', marginBottom:16, fontFamily:font.body }}>
          "{mode === 'login'
            ? "I have hundreds of formula notes. I type a keyword and Jötr finds the exact line in under two seconds."
            : "Finally a note app I actually use. The search is unreal — I found a note I wrote 8 months ago in seconds."}"
        </p>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:'50%', background:C.amber100, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:font.head, fontWeight:800, fontSize:13, color:C.amber500 }}>
            {mode === 'login' ? 'AO' : 'EA'}
          </div>
          <div>
            <p style={{ fontSize:13, fontWeight:700, color:C.white, margin:0, fontFamily:font.body }}>
              {mode === 'login' ? 'Adaeze Okonkwo' : 'Emeka Adeyemi'}
            </p>
            <p style={{ fontSize:12, color:'rgba(255,255,255,0.4)', margin:0, fontFamily:font.body }}>
              {mode === 'login' ? 'Business Analyst, Lagos' : 'MBA Student, Abuja'}
            </p>
          </div>
        </div>
      </div>

      {['Full-text search across all notes', 'Cloud sync — every device', 'Free forever, no credit card'].map(f => (
        <div key={f} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          <div style={{ width:20, height:20, borderRadius:6, background:'rgba(245,166,35,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Check size={11} color={C.amber300}/>
          </div>
          <span style={{ fontSize:13, color:'rgba(255,255,255,0.55)', fontFamily:font.body }}>{f}</span>
        </div>
      ))}
    </div>
  </div>
);

// ─── Password field with show/hide toggle ─────────────────────────────────────
const PwField: React.FC<{ label:string; id:string; value:string; onChange:(v:string)=>void; error?:string; placeholder?:string }> = ({ label, id, value, onChange, error, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
      <label htmlFor={id} style={{ fontSize:13, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>{label}</label>
      <div style={{ position:'relative' }}>
        <input id={id} type={show?'text':'password'} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder||'••••••••'} className={error?'err':''} style={{ paddingRight:46 }}/>
        <button type="button" onClick={()=>setShow(s=>!s)} style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:C.muted, display:'flex', padding:2 }}>
          {show ? <EyeOff size={16}/> : <Eye size={16}/>}
        </button>
      </div>
      {error && <div style={{ display:'flex', alignItems:'center', gap:5 }}><AlertCircle size={12} color={C.red}/><span style={{ fontSize:12, color:C.red, fontFamily:font.body }}>{error}</span></div>}
    </div>
  );
};

// ─── OR divider ───────────────────────────────────────────────────────────────
const Divider: React.FC = () => (
  <div style={{ display:'flex', alignItems:'center', gap:12, margin:'20px 0' }}>
    <div style={{ flex:1, height:1, background:C.border }}/>
    <span style={{ fontSize:12, color:C.muted, fontFamily:font.body, whiteSpace:'nowrap' }}>or continue with email</span>
    <div style={{ flex:1, height:1, background:C.border }}/>
  </div>
);

// ─── Google button ────────────────────────────────────────────────────────────
const GoogleBtn: React.FC<{ text:string }> = ({ text }) => {
  const [loading, setLoading] = React.useState(false);

  const handleGoogleSignIn = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      alert('Google sign-in is not configured yet. Please use email and password.');
      return;
    }
    setLoading(true);
    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => {
      (window as any).google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: any) => {
          try {
            const { authApi } = await import('../api/client');
            const data = await authApi.googleSignIn(response.credential);
            useStore.getState().login(data.user.email, '');
            // Store token directly
            const { setToken } = await import('../api/client');
            setToken(data.token);
            useStore.setState({ user: data.user, isAuthenticated: true, authLoading: false });
            window.location.replace('/dashboard');
          } catch (err: any) {
            alert(err?.message || 'Google sign-in failed');
            setLoading(false);
          }
        },
      });
      (window as any).google.accounts.id.prompt();
      setLoading(false);
    };
    script.onerror = () => { setLoading(false); };
    document.head.appendChild(script);
  };

  return (
    <button type="button" onClick={handleGoogleSignIn} disabled={loading}
      style={{ width:'100%', height:48, borderRadius:10, border:`1.5px solid ${C.border}`, background:C.white, display:'flex', alignItems:'center', justifyContent:'center', gap:10, cursor:loading?'wait':'pointer', fontFamily:font.body, fontWeight:600, fontSize:15, color:C.inkLight, transition:'border-color .15s', opacity: loading?0.7:1 }}
      onMouseEnter={e=>{if(!loading)(e.currentTarget as HTMLButtonElement).style.borderColor=C.amber300;}}
      onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=C.border;}}>
      <GoogleIcon/> {loading ? 'Loading…' : text}
    </button>
  );
};

// ─── Password strength ────────────────────────────────────────────────────────
function pwStrength(p:string){ let s=0; if(p.length>=8)s++; if(/[A-Z]/.test(p))s++; if(/[0-9]/.test(p))s++; if(/[^A-Za-z0-9]/.test(p))s++; return s; }
const StrengthBar: React.FC<{ password:string }> = ({ password }) => {
  if (!password) return null;
  const s = pwStrength(password);
  const colors = ['','#DC2626','#F59E0B','#F5A623','#16A34A'];
  const labels = ['','Weak','Fair','Good','Strong'];
  return (
    <div style={{ marginTop:6 }}>
      <div style={{ display:'flex', gap:4, marginBottom:5 }}>
        {[1,2,3,4].map(i => <div key={i} style={{ flex:1, height:3, borderRadius:2, background: i<=s ? colors[s] : C.border, transition:'background .2s' }}/>)}
      </div>
      <span style={{ fontSize:11, color:colors[s], fontFamily:font.body, fontWeight:600 }}>{labels[s]}</span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// LOGIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export const LoginPage: React.FC = () => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors]     = useState<Record<string,string>>({});
  const [loading, setLoading]   = useState(false);
  const [shake, setShake]       = useState(false);
  const [success, setSuccess]   = useState(false);

  const validate = () => {
    const e: Record<string,string> = {};
    if (!email) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email address';
    if (!password) e.password = 'Password is required';
    else if (password.length < 6) e.password = 'Password must be at least 6 characters';
    return e;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); setShake(true); setTimeout(()=>setShake(false),400); return; }
    setErrors({}); setLoading(true);
    try {
      await useStore.getState().login(email, password);
      setLoading(false); setSuccess(true);
      setTimeout(() => { window.location.replace('/dashboard'); }, 500);
    } catch (err:any) {
      setLoading(false);
      setErrors({ general: err?.message || 'Invalid email or password' });
      setShake(true); setTimeout(()=>setShake(false),400);
    }
  };

  return (
    <>
      <GlobalStyles/>
      <div className="auth-grid">
        <AuthPanel mode="login"/>
        <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', padding:'40px 24px', background:C.white, minHeight:'100vh' }}>
          <div className={`auth-card${shake?' shake':''}`} style={{ width:'100%', maxWidth:420 }}>

            <a href="/" style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:C.muted, textDecoration:'none', marginBottom:36, fontFamily:font.body, fontWeight:500 }}>
              <ArrowLeft size={14}/> Back to Jötr
            </a>

            <div style={{ marginBottom:28 }}>
              <div style={{ marginBottom:20 }}><Logo/></div>
              <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(24px,3vw,32px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:6 }}>Welcome back</h1>
              <p style={{ fontSize:14, color:C.muted, fontFamily:font.body }}>
                Don't have an account? <a href="/signup" style={{ color:C.amber400, fontWeight:600, textDecoration:'none' }}>Sign up free</a>
              </p>
            </div>

            {success && (
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 16px', borderRadius:10, background:C.greenBg, border:`1px solid ${C.green}44`, marginBottom:20 }}>
                <Check size={16} color={C.green}/><span style={{ fontSize:14, color:C.green, fontFamily:font.body, fontWeight:600 }}>Logged in! Taking you to your notes…</span>
              </div>
            )}

            <GoogleBtn text="Continue with Google"/>
            <Divider/>

            <form onSubmit={handleSubmit} noValidate style={{ display:'flex', flexDirection:'column', gap:18 }}>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                <label htmlFor="email" style={{ fontSize:13, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>Email address</label>
                <input id="email" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" className={errors.email?'err':''}/>
                {errors.email && <div style={{ display:'flex', alignItems:'center', gap:5 }}><AlertCircle size={12} color={C.red}/><span style={{ fontSize:12, color:C.red, fontFamily:font.body }}>{errors.email}</span></div>}
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                <PwField label="Password" id="pw" value={password} onChange={setPassword} error={errors.password}/>
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                  <a href="/forgot-password" style={{ fontSize:12, color:C.amber400, fontWeight:600, textDecoration:'none', fontFamily:font.body }}>Forgot password?</a>
                </div>
              </div>

              {errors.general && (
                <div style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 14px', borderRadius:9, background:C.redBg, border:`1px solid ${C.red}33` }}>
                  <AlertCircle size={14} color={C.red}/><span style={{ fontSize:13, color:C.red, fontFamily:font.body }}>{errors.general}</span>
                </div>
              )}

              <button type="submit" disabled={loading||success} style={{ height:50, borderRadius:12, border:'none', cursor:loading||success?'not-allowed':'pointer', background:success?C.green:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:16, fontFamily:font.body, display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:loading||success?0.85:1, boxShadow:`0 6px 20px ${C.amber300}44`, marginTop:4 }}>
                {loading ? <><Loader size={18} className="spin"/> Signing in…</>
                : success ? <><Check size={18}/> Signed in!</>
                : 'Sign in to Jötr'}
              </button>
            </form>

            <p style={{ fontSize:12, color:C.muted, textAlign:'center', marginTop:24, fontFamily:font.body, lineHeight:1.6 }}>
              By signing in you agree to our <a href="/terms" style={{ color:C.amber400, fontWeight:600, textDecoration:'none' }}>Terms</a> and <a href="/privacy" style={{ color:C.amber400, fontWeight:600, textDecoration:'none' }}>Privacy Policy</a>.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SIGNUP PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export const SignupPage: React.FC = () => {
  const [step, setStep]       = useState<1|2>(1);
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [password, setPass]   = useState('');
  const [confirm, setConfirm] = useState('');
  const [agree, setAgree]     = useState(false);
  const [errors, setErrors]   = useState<Record<string,string>>({});
  const [loading, setLoading] = useState(false);
  const [shake, setShake]     = useState(false);
  const [success, setSuccess] = useState(false);

  const validateStep1 = () => {
    const e: Record<string,string> = {};
    if (!name.trim()) e.name = 'Your name is required';
    if (!email) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email address';
    return e;
  };

  const validateStep2 = () => {
    const e: Record<string,string> = {};
    if (!password) e.password = 'Choose a password';
    else if (password.length < 8) e.password = 'Password must be at least 8 characters';
    else if (pwStrength(password) < 2) e.password = 'Please choose a stronger password';
    if (!confirm) e.confirm = 'Please confirm your password';
    else if (confirm !== password) e.confirm = 'Passwords do not match';
    if (!agree) e.agree = 'Please accept the terms to continue';
    return e;
  };

  const handleStep1 = (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validateStep1();
    if (Object.keys(e).length) { setErrors(e); setShake(true); setTimeout(()=>setShake(false),400); return; }
    setErrors({}); setStep(2);
  };

  const handleStep2 = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validateStep2();
    if (Object.keys(e).length) { setErrors(e); setShake(true); setTimeout(()=>setShake(false),400); return; }
    setErrors({}); setLoading(true);
    try {
      await useStore.getState().signup(name, email, password);
      setLoading(false); setSuccess(true);
      setTimeout(() => { window.location.replace('/welcome'); }, 500);
    } catch (err:any) {
      setLoading(false);
      setErrors({ general: err?.message || 'Could not create account. Please try again.' });
      setShake(true); setTimeout(()=>setShake(false),400);
    }
  };

  return (
    <>
      <GlobalStyles/>
      <div className="auth-grid">
        <AuthPanel mode="signup"/>
        <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', padding:'40px 24px', background:C.white, minHeight:'100vh' }}>
          <div className={`auth-card${shake?' shake':''}`} style={{ width:'100%', maxWidth:420 }}>

            {/* Back button */}
            {step === 2 ? (
              <button onClick={()=>setStep(1)} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:C.muted, background:'none', border:'none', cursor:'pointer', marginBottom:36, fontFamily:font.body, fontWeight:500, padding:0 }}>
                <ArrowLeft size={14}/> Back
              </button>
            ) : (
              <a href="/" style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:C.muted, textDecoration:'none', marginBottom:36, fontFamily:font.body, fontWeight:500 }}>
                <ArrowLeft size={14}/> Back to Jötr
              </a>
            )}

            {/* Step indicator */}
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:28 }}>
              {[{n:1,l:'Your details'},{n:2,l:'Set password'}].map((s,i) => (
                <React.Fragment key={s.n}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ width:26, height:26, borderRadius:'50%', background:step>s.n?C.green:step===s.n?C.amber300:C.border, display:'flex', alignItems:'center', justifyContent:'center', transition:'background .2s' }}>
                      {step > s.n ? <Check size={12} color={C.white}/> : <span style={{ fontSize:11, fontWeight:700, color:step>=s.n?C.white:C.muted, fontFamily:font.head }}>{s.n}</span>}
                    </div>
                    <span style={{ fontSize:12, fontWeight:600, color:step>=s.n?C.ink:C.muted, fontFamily:font.body }}>{s.l}</span>
                  </div>
                  {i < 1 && <div style={{ flex:1, height:1.5, background:step>1?C.amber300:C.border, maxWidth:40, transition:'background .3s' }}/>}
                </React.Fragment>
              ))}
            </div>

            <div style={{ marginBottom:24 }}>
              <div style={{ marginBottom:16 }}><Logo/></div>
              <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(22px,3vw,30px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:6 }}>
                {step === 1 ? 'Create your account' : 'Choose a password'}
              </h1>
              <p style={{ fontSize:14, color:C.muted, fontFamily:font.body }}>
                {step === 1
                  ? <> Already have an account? <a href="/login" style={{ color:C.amber400, fontWeight:600, textDecoration:'none' }}>Log in</a></>
                  : "Almost there — one last step and you're in."}
              </p>
            </div>

            {success && (
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 16px', borderRadius:10, background:C.greenBg, border:`1px solid ${C.green}44`, marginBottom:20 }}>
                <Check size={16} color={C.green}/><span style={{ fontSize:14, color:C.green, fontFamily:font.body, fontWeight:600 }}>Account created! Setting up your workspace…</span>
              </div>
            )}

            {/* ── STEP 1 ── */}
            {step === 1 && (
              <>
                <GoogleBtn text="Sign up with Google"/>
                <Divider/>
                <form onSubmit={handleStep1} noValidate style={{ display:'flex', flexDirection:'column', gap:18 }}>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    <label htmlFor="sname" style={{ fontSize:13, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>Full name</label>
                    <input id="sname" type="text" value={name} onChange={e=>setName(e.target.value)} placeholder="Ada Okonkwo" autoComplete="name" className={errors.name?'err':''}/>
                    {errors.name && <div style={{ display:'flex', alignItems:'center', gap:5 }}><AlertCircle size={12} color={C.red}/><span style={{ fontSize:12, color:C.red, fontFamily:font.body }}>{errors.name}</span></div>}
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    <label htmlFor="semail" style={{ fontSize:13, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>Email address</label>
                    <input id="semail" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" className={errors.email?'err':''}/>
                    {errors.email && <div style={{ display:'flex', alignItems:'center', gap:5 }}><AlertCircle size={12} color={C.red}/><span style={{ fontSize:12, color:C.red, fontFamily:font.body }}>{errors.email}</span></div>}
                  </div>
                  <button type="submit" style={{ height:50, borderRadius:12, border:'none', cursor:'pointer', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:16, fontFamily:font.body, boxShadow:`0 6px 20px ${C.amber300}44` }}>
                    Continue →
                  </button>
                </form>
              </>
            )}

            {/* ── STEP 2 ── */}
            {step === 2 && !success && (
              <form onSubmit={handleStep2} noValidate style={{ display:'flex', flexDirection:'column', gap:18 }}>
                <div>
                  <PwField label="Password" id="spw" value={password} onChange={setPass} error={errors.password} placeholder="At least 8 characters"/>
                  <StrengthBar password={password}/>
                </div>
                <PwField label="Confirm password" id="spw2" value={confirm} onChange={setConfirm} error={errors.confirm} placeholder="Repeat your password"/>

                <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}>
                  <div style={{ position:'relative', flexShrink:0, marginTop:1 }}>
                    <input type="checkbox" checked={agree} onChange={e=>setAgree(e.target.checked)} style={{ position:'absolute', opacity:0, width:'100%', height:'100%', margin:0, cursor:'pointer', zIndex:1 }}/>
                    <div style={{ width:18, height:18, borderRadius:5, border:`1.5px solid ${errors.agree?C.red:agree?C.amber300:C.border}`, background:agree?C.amber300:C.white, display:'flex', alignItems:'center', justifyContent:'center', transition:'all .15s' }}>
                      {agree && <Check size={11} color={C.white}/>}
                    </div>
                  </div>
                  <span style={{ fontSize:13, color:C.muted, fontFamily:font.body, lineHeight:1.55 }}>
                    I agree to Jötr's <a href="/terms" style={{ color:C.amber400, fontWeight:600, textDecoration:'none' }}>Terms</a> and <a href="/privacy" style={{ color:C.amber400, fontWeight:600, textDecoration:'none' }}>Privacy Policy</a>
                  </span>
                </label>
                {errors.agree && <div style={{ display:'flex', alignItems:'center', gap:5 }}><AlertCircle size={12} color={C.red}/><span style={{ fontSize:12, color:C.red, fontFamily:font.body }}>{errors.agree}</span></div>}

                {errors.general && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 14px', borderRadius:9, background:C.redBg, border:`1px solid ${C.red}33` }}>
                    <AlertCircle size={14} color={C.red}/><span style={{ fontSize:13, color:C.red, fontFamily:font.body }}>{errors.general}</span>
                  </div>
                )}

                <button type="submit" disabled={loading} style={{ height:50, borderRadius:12, border:'none', cursor:loading?'not-allowed':'pointer', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:16, fontFamily:font.body, display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:loading?0.85:1, boxShadow:`0 6px 20px ${C.amber300}44` }}>
                  {loading ? <><Loader size={18} className="spin"/> Creating your account…</> : 'Create my free account'}
                </button>

                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <Check size={13} color={C.green}/>
                  <span style={{ fontSize:12, color:C.muted, fontFamily:font.body }}>Free forever · No credit card · Cancel anytime</span>
                </div>
              </form>
            )}

          </div>
        </div>
      </div>
    </>
  );
};